"use client";

import { useEffect, useState } from "react";
import yardSensorsRender from "../../assets/images/yard_sensors_render.png";

export default function SmartDelivery3D() {
  const [mounted, setMounted] = useState(false);
  const [telemetry, setTelemetry] = useState({
    x: 48.8566,
    y: 2.3522,
    vehicles: 4,
    bandwidth: 98.4,
  });

  useEffect(() => {
    setMounted(true);
    
    // Simulate real-time sensor updates for a live, interactive feel
    const interval = setInterval(() => {
      setTelemetry((prev) => ({
        x: Number((prev.x + (Math.random() - 0.5) * 0.0001).toFixed(6)),
        y: Number((prev.y + (Math.random() - 0.5) * 0.0001).toFixed(6)),
        vehicles: Math.random() > 0.7 ? Math.floor(Math.random() * 3) + 2 : prev.vehicles,
        bandwidth: Number((95 + Math.random() * 4.9).toFixed(1)),
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  if (!mounted) return null;

  return (
    <div className="w-full h-full relative group overflow-hidden bg-[#070b13] select-none flex items-center justify-center">
      {/* Cyber Grid CSS Keyframe Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes scan {
          0% {
            top: -5%;
          }
          100% {
            top: 105%;
          }
        }
        @keyframes pulse-soft {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.8;
          }
        }
        @keyframes rotate-slow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .animate-scan {
          animation: scan 4s linear infinite;
        }
        .animate-pulse-soft {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        .animate-rotate-slow {
          animation: rotate-slow 12s linear infinite;
        }
      `}} />

      {/* Cyber Grid Texture Overlay */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none opacity-20"
        style={{
          backgroundImage: "linear-gradient(rgba(249, 115, 22, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(249, 115, 22, 0.15) 1px, transparent 1px)",
          backgroundSize: "20px 20px"
        }}
      />

      {/* Futuristic Vignette / Corner Shadows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_40%,rgba(7,11,19,0.85)_100%)] z-10 pointer-events-none" />

      {/* High-Poly Image */}
      <img
        src={yardSensorsRender}
        alt="Futuristic Logistics Yard Render"
        className="w-full h-full object-cover transform scale-100 group-hover:scale-105 transition-all duration-[3000ms] ease-out filter brightness-[0.75] group-hover:brightness-[0.95]"
      />

      {/* Laser Scanning Line */}
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-orange to-transparent opacity-85 shadow-[0_0_12px_var(--brand-orange),0_0_4px_var(--brand-gold)] z-20 pointer-events-none animate-scan" />

      {/* Sweep overlay light */}
      <div className="absolute left-0 right-0 top-0 h-[80px] bg-gradient-to-b from-brand-orange/10 to-transparent z-20 pointer-events-none animate-scan opacity-40" />

      {/* Tech HUD Corner Brackets */}
      {/* Top Left */}
      <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-brand-orange z-20 pointer-events-none" />
      {/* Top Right */}
      <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-brand-orange z-20 pointer-events-none" />
      {/* Bottom Left */}
      <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-brand-orange z-20 pointer-events-none" />
      {/* Bottom Right */}
      <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-brand-orange z-20 pointer-events-none" />

      {/* Live Telemetry HUD Readings */}
      <div className="absolute bottom-4 left-6 z-20 flex flex-col gap-1 font-mono text-[9px] text-brand-gold/80 tracking-wider pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
          <span className="text-white font-bold">LIDAR FEED ACTIVE</span>
        </div>
        <div>LAT: {telemetry.x}° N</div>
        <div>LON: {telemetry.y}° E</div>
      </div>

      <div className="absolute bottom-4 right-6 z-20 flex flex-col items-end gap-1 font-mono text-[9px] text-brand-gold/80 tracking-wider pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
        <div>DET_TRUCKS: 0{telemetry.vehicles}</div>
        <div>LATENCY: 4.2ms</div>
        <div>BANDWIDTH: {telemetry.bandwidth} GB/S</div>
      </div>

      {/* Radar Scanner Overlay */}
      <div className="absolute top-4 right-6 z-20 pointer-events-none flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity duration-300">
        <svg viewBox="0 0 100 100" className="w-10 h-10 text-brand-orange animate-rotate-slow">
          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 8" />
          <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 4" />
          <line x1="50" y1="50" x2="50" y2="5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>

      {/* Interactive telemetry tags */}
      <div className="absolute top-1/3 left-1/4 z-20 bg-black/60 border border-brand-orange/50 rounded px-1.5 py-0.5 font-mono text-[8px] text-brand-gold tracking-widest pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-brand-orange" />
        <span>GATE_A02 [LOADING]</span>
      </div>

      <div className="absolute top-1/2 right-1/3 z-20 bg-black/60 border border-brand-gold/50 rounded px-1.5 py-0.5 font-mono text-[8px] text-brand-gold tracking-widest pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700 flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-brand-gold" />
        <span>GATE_B01 [ONBOARDING]</span>
      </div>
    </div>
  );
}
